import type { ReactNode } from "react";
import { STATIONS, useGame, type StationId } from "../../game/GameContext";
import { gameCopy } from "../../content";

/* A chart cross-marker: the glyph used for waypoints on the route. */
function ChartMark({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden focusable="false">
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 2v4M10 14v4M2 10h4M14 10h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/* The pressed wax stamp shown once a station is logged. */
export function StampMark({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 44 44"
      className={`motion-safe:animate-[stamp-in_0.45s_var(--ease-out-quart)_both] ${className}`}
      style={{ rotate: "-8deg" }}
      aria-hidden
      focusable="false"
    >
      <circle cx="22" cy="22" r="19" fill="none" className="stroke-signal-deep" strokeWidth="2.5" />
      <circle cx="22" cy="22" r="14.5" fill="none" className="stroke-signal-deep" strokeWidth="1" strokeDasharray="2.5 3" />
      {/* tiny boat inside the seal */}
      <path d="M14 25h16l-2.5 4.5h-11Z" fill="none" className="stroke-signal-deep" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M22 25V13m0 0 5.5 2-5.5 2" fill="none" className="stroke-signal-deep" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

type WaypointProps = {
  station: StationId;
  /** The big question / statement for this stop. */
  title: ReactNode;
  sub?: string;
  /** Contextual hook line shown under the waypoint marker. */
  hook?: string;
  align?: "left" | "center";
  /** Set for the night act. */
  onDark?: boolean;
};

/**
 * The recurring station header of the voyage: marker glyph, place name,
 * the station's question, and the stamped state once logged.
 * Place names are wayfinding on the route, genuinely ordinal game content.
 */
export function Waypoint({ station, title, sub, hook, align = "left", onDark = false }: WaypointProps) {
  const { hasStamp } = useGame();
  const stamped = hasStamp(station);
  const name = STATIONS.find((s) => s.id === station)?.name ?? "";
  const centered = align === "center";
  const inkText = onDark ? "text-night-text" : "text-ink";
  const softText = onDark ? "text-night-text/70" : "text-ink-soft";
  const mutedText = onDark ? "text-night-text/60" : "text-muted";

  return (
    <header className={centered ? "flex flex-col items-center text-center" : ""}>
      <p className={`flex items-center gap-2 font-mono text-xs tracking-wide uppercase ${mutedText}`}>
        <ChartMark className="size-4 shrink-0" />
        {gameCopy.waypointLabel}: {name}
      </p>
      <div className={`mt-4 flex items-start gap-4 ${centered ? "justify-center" : ""}`}>
        <h2 className={`display-type text-[clamp(2.2rem,4.6vw,4.2rem)] ${inkText}`}>{title}</h2>
        {stamped && <StampMark className="mt-1 w-11 shrink-0 md:w-14" />}
      </div>
      {sub && <p className={`mt-4 max-w-[52ch] text-lg ${softText}`}>{sub}</p>}
      {hook && !sub && <p className={`mt-4 max-w-[52ch] text-lg ${softText}`}>{hook}</p>}
      {/* quiet confirmation for screen readers and completionists */}
      <p aria-live="polite" className={`mt-2 font-mono text-xs ${stamped ? mutedText : "sr-only"}`}>
        {stamped ? gameCopy.logged : ""}
      </p>
    </header>
  );
}
